import { useQuery, useMutation } from '@apollo/client';
import { QUERY_GET_USER_PREFERENCES } from 'graphql/queries/generalSettings';
import { MUTATION_CREATE_OR_UPDATE_USER_PREFERENCE } from 'graphql/mutations/generalSettings';
import {
    GetUserSettingsPreferences,
    GetUserSettingsPreferencesVariables,
    CreateOrUpdateUserPreferences,
    CreateOrUpdateUserPreferencesVariables,
    UserSettingsPreferenceData
} from 'types/queries/generalSettings';
import { useCallback, useMemo } from 'react';
import { useUserData } from './useUserData';
import { DataFocusViewPref, DFV_MENU_PREF_KEY } from 'ui-component/records/RecordGrid/components/DataFocusViewButton/components';
import { RECORD_VIEW_MENU_PREF_KEY } from 'ui-component/records/RecordGrid/components/RecordViewButton/components';
import { DATA_FOCUS_VIEW_PREF, RECORD_VIEW_PREF, RecordViewPref } from './useLocalStorageListener';

const RECORD_GRID_MODULE = 'RECORD_GRID_MODULE';

export const useUserPreferences = (recordTypeName?: string, recordTypeId?: number) => {
    const { userId, tenantId, isAuthenticated } = useUserData();

    // Query para obtener las preferencias
    const {
        data: preferencesData,
        loading: loadingPreferences,
        refetch
    } = useQuery<GetUserSettingsPreferences, GetUserSettingsPreferencesVariables>(QUERY_GET_USER_PREFERENCES, {
        variables: {
            componentKey: RECORD_GRID_MODULE,
            recordTypeId // Usar el recordTypeId real
        },
        skip: !isAuthenticated || !recordTypeName,
        fetchPolicy: 'cache-and-network'
    });

    // Mutation para crear/actualizar preferencias
    const [createOrUpdatePreference, { loading: savingPreference }] = useMutation<
        CreateOrUpdateUserPreferences,
        CreateOrUpdateUserPreferencesVariables
    >(MUTATION_CREATE_OR_UPDATE_USER_PREFERENCE);

    // Obtener las preferencias actuales
    const currentPreferences = useMemo(() => {
        if (!preferencesData?.getUserPreferences) return null;
        // getUserPreferences puede ser un array o un objeto individual
        const preferences = preferencesData.getUserPreferences;
        const preference = Array.isArray(preferences) ? preferences[0] : preferences;

        // If menuPrefs are present, set the localStorage values and dispatch the custom event
        if (preference?.preferenceData && typeof preference?.preferenceData !== 'string' && preference?.preferenceData?.menuPrefs) {
            const { DFV_MENU_PREF_KEY: dfvMenuPref, RECORD_VIEW_MENU_PREF_KEY: recordViewMenuPref } = preference.preferenceData?.menuPrefs;
            localStorage.setItem(DFV_MENU_PREF_KEY, dfvMenuPref ?? DATA_FOCUS_VIEW_PREF.OPEN_IN_THIS_TAB);
            localStorage.setItem(RECORD_VIEW_MENU_PREF_KEY, recordViewMenuPref ?? RECORD_VIEW_PREF.OPEN_IN_THIS_TAB);
            window.dispatchEvent(
                new CustomEvent('localStorageChange', {
                    detail: { key: DFV_MENU_PREF_KEY, value: dfvMenuPref ?? DATA_FOCUS_VIEW_PREF.OPEN_IN_THIS_TAB }
                })
            );
            window.dispatchEvent(
                new CustomEvent('localStorageChange', {
                    detail: { key: RECORD_VIEW_MENU_PREF_KEY, value: recordViewMenuPref ?? RECORD_VIEW_PREF.OPEN_IN_THIS_TAB }
                })
            );
        }

        return preference;
    }, [preferencesData]);

    // Obtener los pinned actions desde las preferencias
    const pinnedActions = useMemo(() => {
        if (!currentPreferences?.preferenceData) return [];

        try {
            // preferenceData viene como string JSON desde el servidor
            const parsedData =
                typeof currentPreferences.preferenceData === 'string'
                    ? JSON.parse(currentPreferences.preferenceData)
                    : currentPreferences.preferenceData;

            const data = parsedData as { pinnedActions?: string[] };
            const result = data.pinnedActions || [];
            return result;
        } catch (error) {
            console.error('useUserPreferences - Error parsing preferenceData:', error);
            return [];
        }
    }, [currentPreferences]);

    // Función para guardar las preferencias
    const savePreferences = useCallback(
        async (newPinnedActions?: string[]) => {
            if (!tenantId || !userId || !recordTypeName || !recordTypeId) {
                console.log('useUserPreferences - Missing required data for saving:', {
                    hasTenantId: !!tenantId,
                    hasUserId: !!userId,
                    hasRecordTypeName: !!recordTypeName,
                    hasRecordTypeId: !!recordTypeId
                });
                return;
            }

            const DFVMenuPref = localStorage.getItem(DFV_MENU_PREF_KEY) as DataFocusViewPref | null;
            const RecordViewMenuPref = localStorage.getItem(RECORD_VIEW_MENU_PREF_KEY) as RecordViewPref | null;

            const preferenceData: UserSettingsPreferenceData = {
                pinnedActions: newPinnedActions ?? pinnedActions,
                menuPrefs: {
                    DFV_MENU_PREF_KEY: (DFVMenuPref ?? DATA_FOCUS_VIEW_PREF.OPEN_IN_THIS_TAB) as DataFocusViewPref,
                    RECORD_VIEW_MENU_PREF_KEY: (RecordViewMenuPref ?? RECORD_VIEW_PREF.OPEN_IN_THIS_TAB) as RecordViewPref
                }
            };

            try {
                await createOrUpdatePreference({
                    variables: {
                        data: {
                            componentKey: RECORD_GRID_MODULE,
                            name: recordTypeName,
                            preferenceData,
                            recordTypeId, // Usar el recordTypeId real
                            tenantId,
                            userId,
                            isDefault: true
                        }
                    }
                });

                // Refetch para actualizar el cache
                await refetch();
            } catch (error) {
                console.error('useUserPreferences - Error saving preferences:', error);
            }
        },
        [createOrUpdatePreference, tenantId, userId, recordTypeName, recordTypeId, refetch, pinnedActions]
    );

    return {
        pinnedActions,
        savePreferences,
        loading: loadingPreferences || savingPreference,
        hasPreferences: !!currentPreferences
    };
};
